/**
 * SUNDAY FLOOR — predictive-search.js
 * Desktop: opens the full-width overlay panel (a real dialog: focus trap,
 * inert background, Esc/scrim close, focus restoration). Mobile: the same
 * predictive results are injected into a mount already inside the
 * hamburger menu drawer from Stage 2 — that drawer is already a
 * focus-trapped full-screen dialog, so results there need no dialog
 * machinery of their own, and no second mobile search UI is created.
 *
 * Every [data-predictive-search-input] on the page (there are exactly two:
 * the desktop overlay's and the mobile drawer's) is wired identically and
 * generically — this file doesn't know or care which one it's talking to.
 *
 * The underlying <form action="{{ routes.search_url }}"> on both inputs is
 * real and unconditional: if this script fails to load, throws, or a
 * fetch never resolves, pressing Enter still submits a normal GET request
 * to the search results page. Nothing here ever prevents that default
 * form submission.
 */
import { trapFocus, setBackgroundInert } from './global.js';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

const dialogState = { removeTrap: () => {}, lastFocused: null };

function getOverlay() {
  return document.querySelector('[data-search-overlay]');
}

function onOverlayKeydown(e) {
  if (e.key === 'Escape') closeOverlay();
}

function openOverlay() {
  const overlay = getOverlay();
  if (!overlay || !overlay.hidden) return;
  dialogState.lastFocused = document.activeElement;
  overlay.hidden = false;
  document.body.style.overflow = 'hidden';
  setBackgroundInert(overlay, true);
  dialogState.removeTrap = trapFocus(overlay);
  const input = overlay.querySelector('[data-predictive-search-input]');
  if (input) input.focus();
  document.addEventListener('keydown', onOverlayKeydown);
}

function closeOverlay() {
  const overlay = getOverlay();
  if (!overlay || overlay.hidden) return;
  overlay.hidden = true;
  document.body.style.overflow = '';
  setBackgroundInert(overlay, false);
  dialogState.removeTrap();
  document.removeEventListener('keydown', onOverlayKeydown);
  if (dialogState.lastFocused) dialogState.lastFocused.focus();
}

// Open trigger (header search icon, desktop only per its own CSS/markup).
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-search-overlay-trigger]');
  if (!trigger) return;
  if (!getOverlay()) return; // no overlay on the page — let the link navigate to /search normally
  e.preventDefault();
  openOverlay();
});

// Close button and scrim, delegated (not bound once) since this mirrors
// the cart drawer's own reasoning: safer to not assume these nodes are
// never replaced, and delegation costs nothing here either way.
document.addEventListener('click', (e) => {
  if (!e.target.closest('[data-search-overlay-close]')) return;
  closeOverlay();
});

function findResultsMount(input) {
  const container = input.closest('[data-search-overlay], [data-menu-drawer]');
  return container ? container.querySelector('[data-predictive-search-results]') : null;
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function initPredictiveInput(input) {
  if (input.dataset.sfBound) return;
  input.dataset.sfBound = 'true';

  const mount = findResultsMount(input);
  if (!mount) return;

  let debounceTimer = null;
  let controller = null;
  // Incrementing id, checked after every fetch resolves — belt-and-braces
  // alongside AbortController: even if an aborted fetch's catch block
  // still somehow ran (or a very late, non-aborted response arrived out
  // of order), a stale request can never overwrite what a newer keystroke
  // already produced.
  let latestRequestId = 0;

  function clearResults() {
    mount.innerHTML = '';
    mount.classList.remove('is-loading', 'has-error');
  }

  function showLoading() {
    mount.classList.add('is-loading');
    mount.classList.remove('has-error');
  }

  function showError() {
    mount.classList.remove('is-loading');
    mount.classList.add('has-error');
    mount.innerHTML = `<p class="sf-t-small sf-predictive-search__error">${escapeHtml(
      input.dataset.errorMessage || 'Something went wrong. Try again.'
    )}</p>`;
  }

  async function performSearch(query) {
    const thisRequestId = ++latestRequestId;

    if (controller) controller.abort();
    controller = new AbortController();
    showLoading();

    const url = new URL('/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('section_id', 'predictive-search');

    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      const html = await response.text();

      // A newer keystroke already started a request while this one was
      // in flight — its result, whenever it lands, is what should win.
      if (thisRequestId !== latestRequestId) return;
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-predictive-search-content]');
      mount.classList.remove('is-loading', 'has-error');
      mount.innerHTML = newContent ? newContent.outerHTML : '';
    } catch (err) {
      if (err.name === 'AbortError') return; // superseded, not a real failure
      if (thisRequestId !== latestRequestId) return;
      showError();
    }
  }

  input.addEventListener('input', () => {
    const query = input.value.trim();
    window.clearTimeout(debounceTimer);

    if (query.length < MIN_CHARS) {
      if (controller) controller.abort();
      latestRequestId += 1; // invalidate any in-flight request immediately
      clearResults();
      return;
    }

    debounceTimer = window.setTimeout(() => performSearch(query), DEBOUNCE_MS);
  });

  // Arrow-down from the input moves focus into the first result, so the
  // results list is keyboard-reachable without relying on Tab order alone
  // once results exist below the field.
  input.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown') return;
    const firstLink = mount.querySelector('a');
    if (firstLink) {
      e.preventDefault();
      firstLink.focus();
    }
  });
}

document.querySelectorAll('[data-predictive-search-input]').forEach(initPredictiveInput);
